import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import DefaultApiLimit from '../../../../config/defaultLimit';
import {
  ConsumerScheduleUpcomingInterFace,
  RequestConsumerScheduleUpcomingInterFace,
} from '../../../../interface/end-consumer-schedule-upcoming-interface';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setSelectedDevice } from '../../../../redux/slices/DeviceManagementSlice';
import {
  fetchConsumerScheduleUpcoming,
  fetchConsumerScheduleUpcomingFilter,
  setSortData,
  updateConsumerScheduleUpcomingListFilter,
} from '../../../../redux/slices/EndConsumerScheduleUpcoming';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import CustomTable from '../../../../shared-components/custom-table/CustomTable';
import DeviceSidePanel from '../../../../shared-components/table-side-panel/DeviceSidePanel';
import SidePaneModel from '../../../../shared-components/table-side-panel/SidePaneModel';
import { GetParametersFromCommonFilters } from '../../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../../shared-functions/ConvertUTCtoDate';
import { convertUtcTo12HourTime } from '../../../../shared-functions/DateTime';
import { exportCSV, exportPDF } from '../../../../shared-functions/ExportFile';
import userApiV2 from '../../../../utils/api/end-consumer-v2';

/**
 * Charge Session List
 * @returns {any} - The rendered schedule list component.
 */
function ScheduleUpcomingList() {
  const { t, i18n } = useTranslation('endConsumer');
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const { selectedEndConsumer, endConsumerType } = useAppSelector((state) => state.endConsumerType);
  const dispatch = useAppDispatch();
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [modelDetails, setModelDetails] = useState({
    content: <div />,
    url: '',
    urlNewTab: '',
  });
  const {
    consumers,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.ConsumerScheduleUpcoming);

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: RequestConsumerScheduleUpcomingInterFace) => {
    if (!loading && selectedEndConsumer) {
      dispatch(
        fetchConsumerScheduleUpcoming({
          userCustomId: selectedEndConsumer.userCustomId,
          status: 'upcoming',
          ...data,
        }),
      );
    }
  };

  /**
   * Handle open device details
   * @param {any} data - {ConsumerScheduleUpcomingInterFace}
   */
  const handleOpenDeviceDetails = (data: ConsumerScheduleUpcomingInterFace) => {
    setIsOpen(true);
    dispatch(
      setSelectedDevice({
        id: data?.chargingPointId,
        name: data?.chargingPointName,
        chargingPointSn: data?.chargingPointSn,
      }),
    );
    setModelDetails({
      content: <DeviceSidePanel fetchIdFrom="queryParams" />,
      url: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
      urlNewTab: `/ev-admin/end-consumer/${endConsumerType}/profile/${selectedEndConsumer?.userCustomId}/charger-expanded/${data?.chargingPointId}/device-details?searchBy=${data?.chargingPointName}`,
    });
  };

  const column = [
    {
      label: t('startDateTime'),
      accessorKey: 'scheduleStartDateUtc' as keyof ConsumerScheduleUpcomingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerScheduleUpcomingInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.scheduleStartDateUtc)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.scheduleStartDateUtc)}</p>
        </div>
      ),
    },
    {
      label: t('endDateTime'),
      accessorKey: 'scheduleEndDateUtc' as keyof ConsumerScheduleUpcomingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerScheduleUpcomingInterFace) => (
        <div>
          <p>{ConvertUTCtoDate(currentRow?.scheduleEndDateUtc)}</p>
          <p>{convertUtcTo12HourTime(currentRow?.scheduleEndDateUtc)}</p>
        </div>
      ),
    },
    {
      label: t('device'),
      accessorKey: 'chargingPointSn' as keyof ConsumerScheduleUpcomingInterFace,
      sortAllow: false,
      /**
       * Rendering device name as hyper link
       * @param {any} currentRow - current row data
       * @returns {any} - rendered device name
       */
      customData: (currentRow: ConsumerScheduleUpcomingInterFace) => (
        <p
          role="presentation"
          className="hyper-link"
          onClick={() => handleOpenDeviceDetails(currentRow)}>
          {currentRow?.chargingPointSn}
        </p>
      ),
    },
    // {
    //   label: t('vehicle'),
    //   accessorKey: 'vehicle' as keyof ConsumerScheduleUpcomingInterFace,
    //   sortAllow: false,
    // },
    {
      label: t('setPowerOutput'),
      accessorKey: 'maxPowerOutput' as keyof ConsumerScheduleUpcomingInterFace,
      sortAllow: true,
      /**
       * Custom data function
       * @param {any} currentRow - current row data
       * @returns {any} - custom data
       */
      customData: (currentRow: ConsumerScheduleUpcomingInterFace) => (
        <div className="value-container">
          <p className="value">
            {currentRow?.customsMaxPower
              ? `${currentRow?.maxPowerOutput} `
              : `${currentRow?.maximumOutputPower}`}
          </p>
          <span>{currentRow?.maxPowerOutputUnit ? currentRow.maxPowerOutputUnit : 'A'}</span>
        </div>
      ),
    },
  ];

  /**
   * Current columns for Schedule Upcoming export
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },
    { label: t('startDateTime'), accessorKey: 'scheduleStartDateUtc' },
    { label: t('endDateTime'), accessorKey: 'scheduleEndDateUtc' },
    { label: t('device'), accessorKey: 'chargingPointSn' },
    // { label: t('vehicle'), accessorKey: 'vehicle' },
    { label: t('setPowerOutput'), accessorKey: 'maxPowerOutput' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    setExportLoader(true);

    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload: any = { ...queryParameters };

    if (sortField) payload.sortField = sortField;
    if (sortBy) payload.sortBy = sortBy;

    payload.status = 'upcoming';

    if (!selectedEndConsumer?.userCustomId) return;

    userApiV2
      .fetchSchedulesExportData(selectedEndConsumer.userCustomId, payload)
      .then((response) => {
        /**
         * Dynamic Headers
         */
        const headers = exportCurrentColumns.map((col) => col.label);

        /**
         * Dynamic Rows
         */
        const rows = response.data.map((row: ConsumerScheduleUpcomingInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'scheduleStartDateUtc':
                return row?.scheduleStartDateUtc
                  ? `${ConvertUTCtoDate(
                      row.scheduleStartDateUtc,
                    )} ${convertUtcTo12HourTime(row.scheduleStartDateUtc)}`
                  : t('na');

              case 'scheduleEndDateUtc':
                return row?.scheduleEndDateUtc
                  ? `${ConvertUTCtoDate(
                      row.scheduleEndDateUtc,
                    )} ${convertUtcTo12HourTime(row.scheduleEndDateUtc)}`
                  : t('na');

              case 'chargingPointSn':
                return row?.chargingPointSn || t('na');

              // case 'vehicle':
              //   return row?.vehicle || 'N/A';

              case 'maxPowerOutput':
                return row.customsMaxPower
                  ? `${row.maxPowerOutput} ${row?.maxPowerOutputUnit}`
                  : `${row.maximumOutputPower} A`;

              default:
                return (row as any)[col.accessorKey] || t('na');
            }
          }),
        );

        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'reports-list.csv');
          } else {
            exportPDF(headers, rows, 'reports-list.pdf');
          }
        }

        setExportLoader(false);
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Table pagination function
   * @param {any} value - pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (!selectedEndConsumer?.userCustomId) return;

    const currentId = selectedEndConsumer.userCustomId;
    const lastId = sessionStorage.getItem('scheduleUpcomingList');

    if (isFilterUpdated || consumers.length === 0 || lastId !== currentId) {
      if (selectedEndConsumer?.userCustomId) {
        const queryParameters = GetParametersFromCommonFilters(filters);
        fetchData({
          page: DefaultApiLimit.page,
          limit,
          ...queryParameters,
        });
      }
    }
    sessionStorage.setItem('scheduleUpcomingList', currentId);
  }, [filters, selectedEndConsumer]);

  return (
    <div dir={i18n.dir()}>
      <SidePaneModel
        open={isOpen}
        content={modelDetails.content}
        closePanel={() => setIsOpen(false)}
        url={modelDetails.url}
        urlNewTab={modelDetails.urlNewTab}
      />
      <CustomTable
        dataKey="EndConsumerManagement"
        header={column}
        data={consumers}
        serialNo
        actionButton
        sizeChangeButtonRequired
        version="v2"
        height={500}
        page={page}
        limit={limit}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        filters={filters}
        filtersToDispatch={updateConsumerScheduleUpcomingListFilter}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchConsumerScheduleUpcomingFilter}
        exportData={consumers}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        refreshRequired
        requiredRefreshToggle
        refershCurrentPages={handleRefresh}
        refreshFunction={handleRefresh}
        refreshInterval={10}
        hoverLabel={t('refresh')}
        leftTopContent={<> </>}
        expandTableOpen
        expandTableOpenKey="ScheduleUpcomingV2ListEndConsumer"
        onHiddenColumnsChange={setHiddenColumns}
      />{' '}
    </div>
  );
}

export default ScheduleUpcomingList;
